import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPurchases, createPurchase, removePurchase, updatePurchase } from '../features/purchase/purchaseSlice';
import { fetchSuppliers } from '../features/supplier/supplierSlice';
import { useNavigate } from 'react-router-dom';
import { getProducts } from '../features/product/productSlice';
import { getFiscalStatus } from '../features/fiscal/fiscalSlice';

const defaultItem = () => ({ descripcion: '', cantidad: 1, costo: 0, alicuotaIVACode: 5 });

export default function Purchases() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, sum } = useSelector((s) => s.purchase);
  const suppliers = useSelector((s) => s.supplier.items);
  const products = useSelector((s) => s.product.products || []);
  const fiscalStatus = useSelector((s) => s.fiscal?.fiscalStatus);
  const emitterIsMonotributo = useMemo(() => {
    if (!fiscalStatus) return false;
    const cond = Number(fiscalStatus.condicionIVA || fiscalStatus.codigoCondicionIVA || 0);
    const tipo = String(fiscalStatus.tipoResponsable || '').toLowerCase();
    return cond === 6 || tipo.includes('monotrib');
  }, [fiscalStatus]);
  const productByBarcode = useMemo(() => {
    const map = new Map();
    products.forEach(p => { if (p.barcode) map.set(String(p.barcode), p); });
    return map;
  }, [products]);
  const productById = useMemo(() => {
    const map = new Map();
    products.forEach(p => { if (p._id) map.set(String(p._id), p); });
    return map;
  }, [products]);

  const onSelectProduct = (idx, productId) => {
    const p = productById.get(String(productId));
    if (!p) return;
    updateItem(idx, {
      productId: p._id,
      barcode: p.barcode || '',
      descripcion: p.name || '',
      alicuotaIVACode: emitterIsMonotributo ? 3 : (p.alicuotaIVACode ?? 5),
      costo: (typeof p.costPrice === 'number' ? p.costPrice : (p.cost || 0))
    });
  };

  // Refs para manejo de foco por teclado
  const barcodeRefs = useRef([]);
  const qtyRefs = useRef([]);
  const costRefs = useRef([]);

  const resolveSearch = (term) => {
    const t = String(term || '').trim();
    if (!t) return null;
    // Primero intentar por barcode exacto
    const byCode = productByBarcode.get(t);
    if (byCode) return byCode;
    // Luego por nombre exacto
    const lower = t.toLowerCase();
    const exactByName = products.find(p => String(p.name || '').toLowerCase() === lower);
    if (exactByName) return exactByName;
    // Luego por incluye nombre o barcode
    const contains = products.find(p => (p.name || '').toLowerCase().includes(lower) || String(p.barcode || '').includes(t));
    return contains || null;
  };

  const [filters, setFilters] = useState({ from: '', to: '', supplierId: '', estado: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ supplierId: '', fecha: '', invoiceNumber: '', items: [defaultItem()], notas: '' });

  useEffect(() => {
    dispatch(fetchPurchases({}));
    dispatch(fetchSuppliers({ limit: 1000 }));
    // Pre-cargar productos para validar barcode en cliente y refrescar stock/costo luego
    dispatch(getProducts());
    dispatch(getFiscalStatus());
  }, [dispatch]);

  const onFilter = (e) => { e.preventDefault(); dispatch(fetchPurchases(filters)); };

  const addItemRow = () => setForm((f) => ({ ...f, items: [...f.items, defaultItem()] }));
  const removeItemRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  const onCreate = (e) => {
    e.preventDefault();
    if (!form.supplierId) return alert('Seleccione un proveedor');
    // Validación en cliente: cada ítem debe tener barcode existente
    for (const it of form.items) {
      const code = String(it.barcode || '').trim();
      const hasId = it.productId && productById.get(String(it.productId));
      const hasCode = code && productByBarcode.get(code);
      if (!hasId && !hasCode) return alert('Cada ítem debe seleccionar un producto o ingresar un código válido');
    }
    const payload = {
      supplierId: form.supplierId,
      invoiceNumber: form.invoiceNumber || undefined,
      fecha: form.fecha || new Date().toISOString().slice(0, 10),
      items: form.items.map((it) => ({
        productId: it.productId || undefined,
        barcode: it.barcode || undefined,
        descripcion: it.descripcion,
        cantidad: Number(it.cantidad || 0),
        costo: Number(it.costo || 0),
        alicuotaIVACode: emitterIsMonotributo ? 3 : Number(it.alicuotaIVACode || 5),
      })),
      notas: form.notas,
    };
    if (editingId) {
      dispatch(updatePurchase({ id: editingId, data: payload })).then((res) => {
        if (!res.error) {
          dispatch(getProducts());
          setCreateOpen(false);
          setEditingId(null);
          setForm({ supplierId: '', fecha: '', invoiceNumber: '', items: [defaultItem()], notas: '' });
        }
      });
    } else {
      dispatch(createPurchase(payload)).then((res) => {
        if (!res.error) {
          // Refrescar productos para ver stock/costo actualizados
          dispatch(getProducts());
          setCreateOpen(false);
          setForm({ supplierId: '', fecha: '', invoiceNumber: '', items: [defaultItem()], notas: '' });
        }
      });
    }
  };

  const computedSubtotal = useMemo(() => form.items.reduce((acc, it) => acc + (Number(it.cantidad || 0) * Number(it.costo || 0)), 0), [form.items]);

  const onDelete = (id) => { if (window.confirm('¿Eliminar compra?')) dispatch(removePurchase(id)); };

  const loadForEdit = (p) => {
    // Map purchase object to form structure
    const mappedItems = (p.items || []).map(it => ({
      descripcion: it.descripcion || '',
      cantidad: it.cantidad || 1,
      costo: it.costo || 0,
      alicuotaIVACode: it.alicuotaIVACode || 5,
      productId: it.productId || undefined,
      barcode: it.barcode || ''
    }));
    setForm({
      supplierId: p.supplierId?._id || (typeof p.supplierId === 'string' ? p.supplierId : ''),
      fecha: p.fecha ? new Date(p.fecha).toISOString().slice(0,10) : '',
      invoiceNumber: p.invoiceNumber || '',
      items: mappedItems.length ? mappedItems : [defaultItem()],
      notas: p.notas || ''
    });
    setEditingId(p._id);
    setCreateOpen(true);
  };

  return (
    <div className="container-safe" style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/dashboard')} style={btn('#718096')}>← Volver</button>
          <h2 style={{ margin: 0 }}>🧾 Compras</h2>
        </div>
        <div>
          <button onClick={() => { setEditingId(null); setForm({ supplierId: '', fecha: '', invoiceNumber: '', items: [defaultItem()], notas: '' }); setCreateOpen(true); }} style={btn('#28a745')}>➕ Nueva compra</button>
        </div>
      </div>

      <form onSubmit={onFilter} className="stack-sm" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} style={input} />
        <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} style={input} />
        <select value={filters.supplierId} onChange={(e) => setFilters({ ...filters, supplierId: e.target.value })} style={input}>
          <option value="">Todos los proveedores</option>
          {suppliers.map((s) => (
            <option key={s._id} value={s._id}>{s.nombre} {s.docNro ? `(${s.docNro})` : ''}</option>
          ))}
        </select>
        {/* Removed unused 'Todos los estados' dropdown per request */}
        <button type="submit" style={btn('#667eea')}>Filtrar</button>
      </form>

      <div style={card}>
        {loading ? (
          <p style={{ padding: 20 }}>Cargando...</p>
        ) : items.length === 0 ? (
          <p style={{ padding: 20, textAlign: 'center', color: '#666' }}>No hay compras</p>
        ) : (
          <div className="responsive-table">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={th}>Fecha</th>
                  <th style={th}>Proveedor</th>
                  <th style={th}>Items</th>
                  <th style={th}>Total</th>
                  <th style={th}>Estado</th>
                  <th style={th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p._id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={td}>{new Date(p.fecha).toLocaleDateString()}</td>
                    <td style={td}>{p.supplierId?.nombre || '-'}</td>
                    <td style={td}>{(p.items || []).length}</td>
                    <td style={{ ...td, textAlign: 'right' }}>${Number(p.total || 0).toFixed(2)}</td>
                    <td style={td}>{p.estado}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {/* Acciones: editar / eliminar */}
                      <button onClick={() => loadForEdit(p)} style={btn('#f59e0b')}>✏️ Editar</button>
                      <button onClick={() => onDelete(p._id)} style={{ ...btn('#dc3545'), marginLeft: 8 }}>🗑️ Eliminar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, textAlign: 'right', color: '#333' }}>
        <strong>Total período:</strong> ${Number(sum).toFixed(2)}
      </div>

      {createOpen && (
        <div style={overlay} onClick={() => { setCreateOpen(false); setEditingId(null); }}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Nueva compra</h3>
            <form onSubmit={onCreate}>
              <div className="form-grid" style={{ marginBottom: 12 }}>
                <label style={lbl}>Proveedor
                  <select value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })} style={input} required>
                    <option value="" disabled>Seleccione...</option>
                    {suppliers.map((s) => <option key={s._id} value={s._id}>{s.nombre} {s.docNro ? `(${s.docNro})` : ''}</option>)}
                  </select>
                </label>
                <label style={lbl}>Fecha
                  <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} style={input} />
                </label>
                <label style={lbl}>Nº Factura / Invoice
                  <input type="text" value={form.invoiceNumber || ''} onChange={(e) => setForm({ ...form, invoiceNumber: e.target.value })} style={input} placeholder="Nº factura" />
                </label>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Items</strong>
                  <button type="button" onClick={addItemRow} style={btn('#28a745')}>➕ Agregar ítem</button>
                </div>
                <div className="responsive-table" style={{ marginTop: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <th style={th}>Producto</th>
                        <th style={th}>Código</th>
                        <th style={th}>Descripción</th>
                        <th style={th}>Cant.</th>
                        <th style={th}>Costo</th>
                        {!emitterIsMonotributo && <th style={th}>IVA</th>}
                        <th style={th}></th>
                      </tr>
                    </thead>
                    <tbody>
                    {form.items.map((it, idx) => (
                      <tr key={idx}>
                        <td style={td}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <input
                              list="product-suggestions"
                              value={it.search || ''}
                              onChange={(e) => updateItem(idx, { search: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const p = resolveSearch(e.currentTarget.value);
                                  if (p) onSelectProduct(idx, p._id);
                                }
                              }}
                              placeholder="Buscar nombre/sku"
                              style={input}
                            />
                            <select value={it.productId || ''} onChange={(e) => onSelectProduct(idx, e.target.value)} style={input}>
                              <option value="">Seleccione producto…</option>
                              {products.map(p => (
                                <option key={p._id} value={p._id}>{p.name} {p.barcode ? `(${p.barcode})` : ''}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td style={td}>
                          <input
                            ref={(el) => (barcodeRefs.current[idx] = el)}
                            value={it.barcode || ''}
                            onChange={(e) => updateItem(idx, { barcode: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const code = String(e.currentTarget.value || '').trim();
                                const p = productByBarcode.get(code);
                                if (p) {
                                  updateItem(idx, {
                                    productId: p._id,
                                    barcode: p.barcode || code,
                                    descripcion: p.name || it.descripcion || '',
                                    alicuotaIVACode: emitterIsMonotributo ? 3 : (p.alicuotaIVACode ?? it.alicuotaIVACode ?? 5),
                                    cantidad: Number(it.cantidad || 1),
                                    costo: (typeof p.costPrice === 'number' ? p.costPrice : (p.cost || it.costo || 0))
                                  });
                                  // Mover foco a cantidad para agilizar carga
                                  setTimeout(() => {
                                    const ref = qtyRefs.current[idx];
                                    if (ref && typeof ref.focus === 'function') ref.focus();
                                  }, 0);
                                }
                              }
                            }}
                            style={input}
                            placeholder="Barcode"
                          />
                          {it.barcode ? (
                            productByBarcode.get(String(it.barcode)) ? (
                              <div style={{ fontSize: 12, color: '#2b6cb0', marginTop: 4 }}>
                                {productByBarcode.get(String(it.barcode)).name}
                              </div>
                            ) : (
                              <div style={{ fontSize: 12, color: '#e53e3e', marginTop: 4 }}>
                                Producto no encontrado
                              </div>
                            )
                          ) : null}
                        </td>
                        <td style={td}><input value={it.descripcion} onChange={(e) => updateItem(idx, { descripcion: e.target.value })} style={input} placeholder="Descripción" /></td>
                        <td style={td}>
                          <input
                            ref={(el) => (qtyRefs.current[idx] = el)}
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={it.cantidad}
                            onChange={(e) => updateItem(idx, { cantidad: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const ref = costRefs.current[idx];
                                if (ref && typeof ref.focus === 'function') ref.focus();
                              }
                            }}
                            style={input}
                          />
                        </td>
                        <td style={td}>
                          <input
                            ref={(el) => (costRefs.current[idx] = el)}
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={it.costo}
                            onChange={(e) => updateItem(idx, { costo: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                // Agregar nueva fila y enfocar el barcode de la nueva fila
                                const newIdx = form.items.length; // índice de la nueva fila
                                setForm((f) => ({ ...f, items: [...f.items, defaultItem()] }));
                                setTimeout(() => {
                                  const nextRef = barcodeRefs.current[newIdx];
                                  if (nextRef && typeof nextRef.focus === 'function') nextRef.focus();
                                }, 0);
                              }
                            }}
                            style={input}
                          />
                          {/* Costo sugerido informativo */}
                          {(() => {
                            const p = it.productId ? productById.get(String(it.productId)) : (it.barcode ? productByBarcode.get(String(it.barcode)) : null);
                            const sug = p && (typeof p.costPrice === 'number' ? p.costPrice : p.cost);
                            if (p && typeof sug === 'number' && !Number.isNaN(sug)) {
                              const diff = Number(it.costo || 0) !== Number(sug || 0);
                              return (
                                <div style={{ fontSize: 11, color: diff ? '#6b7280' : '#4b5563', marginTop: 4 }}>
                                  Sugerido: ${Number(sug).toFixed(2)}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </td>
                        {!emitterIsMonotributo && (
                          <td style={td}>
                            <select value={it.alicuotaIVACode} onChange={(e) => updateItem(idx, { alicuotaIVACode: Number(e.target.value) })} style={input}>
                              <option value={3}>0%</option>
                              <option value={4}>10.5%</option>
                              <option value={5}>21%</option>
                              <option value={6}>27%</option>
                              <option value={8}>5%</option>
                              <option value={9}>2.5%</option>
                            </select>
                          </td>
                        )}
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button type="button" onClick={() => removeItemRow(idx)} style={btn('#dc3545')}>🗑️</button>
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
                {/* Sugerencias globales para el buscador */}
                <datalist id="product-suggestions">
                  {products.map(p => (
                    <option key={p._id} value={p.name} />
                  ))}
                </datalist>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div />
                <div style={{ fontWeight: 700 }}>Subtotal (estimado): ${computedSubtotal.toFixed(2)}</div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setCreateOpen(false); setEditingId(null); }} style={btn('#6c757d')}>Cancelar</button>
                <button type="submit" style={btn('#667eea')}>{editingId ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const card = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' };
const th = { textAlign: 'left', padding: 10, fontWeight: 700, fontSize: 13 };
const td = { textAlign: 'left', padding: 10, fontSize: 13 };
const input = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, width: '100%' };
const lbl = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#555' };
const btn = (bg) => ({ padding: '8px 12px', background: bg, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' });
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal = { background: '#fff', borderRadius: 12, padding: 20, width: 900, maxWidth: '95vw' };
